import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo } from 'react';

import AuthErrorView from '../../components/_organisms/Auth/AuthErrorView';
import { newRelicNoticeError } from '../../hooks/useNewRelic';
import APILogger from '../../services/APILogger';
import { AuthenticationError, AuthError, AuthErrorType } from '../../types/auth';
import { mapNextAuthCodeToType } from '../../utils/authErrorMap';
import { clearAuthSessionCookie } from '../../utils/clearAuthSessionCookie';
import { detectAndBestEffortSetLanguage } from '../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../utils/react-query-utils';
import { getRotatingServerSession } from '../../utils/serverSession';
import { provisionFlags } from '../../utils/ServerSideFeatureFlagUtils';

const AuthErrorPage = ({ correlationId }: { correlationId?: string }) => {
  const router = useRouter();

  // Two query shapes feed this page:
  //  - `error_type` (+ message/correlation): the provisioning recovery path sets
  //    these directly.
  //  - `error`: NextAuth's native OAuth failure code (via pages.error). Map it.
  // The reference id is authoritative from GSSP (it logged it server-side), falling
  // back to the query so a shared/bookmarked link still shows something sensible.
  const error: AuthError | undefined = useMemo(() => {
    const errorType = router.query.error_type as AuthErrorType | undefined;
    const nativeCode = router.query.error as string | undefined;
    const type =
      errorType ?? (nativeCode ? mapNextAuthCodeToType(nativeCode) : undefined);
    if (!type) return undefined;
    return {
      type,
      message: router.query.error_message as string | undefined,
      originalError: (router.query.original_error as string) ?? nativeCode,
      correlationId: correlationId ?? (router.query.correlation_id as string | undefined),
    };
  }, [
    router.query.error_type,
    router.query.error,
    router.query.error_message,
    router.query.original_error,
    router.query.correlation_id,
    correlationId,
  ]);

  // Report once per distinct error (effect, not render body, to avoid duplicate
  // notices under React 18 strict/concurrent renders).
  useEffect(() => {
    if (error) newRelicNoticeError(new AuthenticationError(error));
  }, [error]);

  return (
    <>
      <Head>
        <meta name="robots" content="noindex, nofollow" />
      </Head>
      {error && <AuthErrorView error={error} />}
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  context.res.setHeader(
    'Cache-Control',
    'no-store, no-cache, must-revalidate, proxy-revalidate',
  );

  const session = await getRotatingServerSession(context.req, context.res);
  const queryClient = new QueryClient();
  const flags = await provisionFlags(context, queryClient, session);

  // An errored session (e.g. a new-auth ProvisioningError) reached this page. Drop
  // it server-side so the client never sees it — otherwise the recovery sign-out
  // would fire and reload the page (a visible flash). The error UI is driven by
  // the query params, not the session, so clearing it costs nothing here.
  const hasErroredSession = !!session?.error;
  if (hasErroredSession) {
    clearAuthSessionCookie(context.res, context.req);
  }

  // Establish a reference id the user can screenshot and that we log server-side,
  // so support can trace any auth failure (especially OAuth provider handshakes,
  // whose root cause NextAuth logs separately by code + provider + timestamp).
  // Reuse an upstream correlation id (provisioning recovery) when present.
  const hasError = !!(context.query.error_type || context.query.error);
  let correlationId = (context.query.correlation_id as string | undefined) || undefined;
  if (hasError) {
    if (!correlationId) {
      // Deterministic, dependency-free unique id (uuid-ish) for the GSSP runtime.
      correlationId = `auth_${Date.now().toString(36)}_${Math.random()
        .toString(36)
        .slice(2, 10)}`;
    }
    const logger = new APILogger();
    logger.setTraceId(correlationId);
    logger.error('AUTH_ERROR_PAGE', {
      correlationId,
      errorType: (context.query.error_type as string) ?? null,
      nativeCode: (context.query.error as string) ?? null,
      userAgent: context.req.headers['user-agent'] ?? null,
      referer: context.req.headers['referer'] ?? null,
    });
  }

  return {
    props: {
      ...flags,
      correlationId: correlationId ?? null,
      dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
      ...(await detectAndBestEffortSetLanguage(context, { routeClass: 'app' })),
      // Don't hydrate the client with an errored session (we just cleared its
      // cookie) — handing it to SessionProvider would re-trigger the recovery
      // sign-out and the flash we're removing.
      ...(session && !session.error && { session }),
    },
  };
};

export default AuthErrorPage;
